// API Service — calls the Zénde CMS public endpoints
// https://zende.africa/api

const CMS_BASE = process.env.REACT_APP_CMS_API_URL || 'https://zende.africa/api';

// ─── In-memory request cache (GET list requests only) ─────────────────────────
// Prevents duplicate fetches when React re-renders or city context refreshes.
// TTL = 2 minutes — matches the Cache-Control max-age on the server.
const _reqCache = new Map();
const _CACHE_TTL = 2 * 60 * 1000; // 2 minutes

function _cacheGet(key)        { const e = _reqCache.get(key); return e && Date.now() - e.ts < _CACHE_TTL ? e.val : null; }
function _cacheSet(key, val)   { _reqCache.set(key, { val, ts: Date.now() }); }
export function clearApiCache() { _reqCache.clear(); } // call on city change if needed

// ─── HTTP helper ──────────────────────────────────────────────────────────────

async function cmsReq(endpoint, options = {}) {
  const res = await fetch(`${CMS_BASE}${endpoint}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (!res.ok) {
    // Try to read the JSON error body for a meaningful message
    let msg = `Server error ${res.status}`;
    try {
      const errJson = await res.json();
      if (errJson.message) msg = errJson.message;
    } catch {}
    throw new Error(msg);
  }
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'CMS API error');
  return json;
}

/** GET-only wrapper that returns a cached promise if one is in-flight or fresh. */
async function cmsReqCached(endpoint) {
  const cached = _cacheGet(endpoint);
  if (cached) return cached;
  const result = await cmsReq(endpoint);
  _cacheSet(endpoint, result);
  return result;
}

// ─── Normalisers ──────────────────────────────────────────────────────────────
// These translate CMS database shapes to the field names the UI components use.

const PRICE_NUM = { '₦': 1, '₦₦': 2, '₦₦₦': 3, '₦₦₦₦': 4 };

/** Build a slug from a name only (no ID suffix — DB stores the canonical slug). */
export function makeSlug(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function fmt24to12(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12  = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2,'0')}${ampm}`;
}

export function normalizePlace(p) {
  // Build ordered photos array from cover_image + place_images
  const photos = [];

  // Add cover image first if it exists
  if (p.cover_image) photos.push(p.cover_image);

  // Add all images from place_images table
  if (Array.isArray(p.images) && p.images.length > 0) {
    [...p.images]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach(img => {
        // Handle both {url: "..."} and {image_url: "..."} formats
        const imgUrl = img.url || img.image_url || img.path;
        if (imgUrl && !photos.includes(imgUrl)) {
          photos.push(imgUrl);
        }
      });
  }

  // Convert [{day, open_time, close_time, is_closed}] → {Monday: '11am – 11pm'}
  // Note: PDO may return is_closed as string "0"/"1" — use Number() to coerce safely
  const hours = {};
  if (Array.isArray(p.hours)) {
    p.hours.forEach(h => {
      hours[h.day] = Number(h.is_closed) ? 'Closed' : `${fmt24to12(h.open_time)} – ${fmt24to12(h.close_time)}`;
    });
  }

  // Convert menu_sections → menu (the shape PlaceDetail uses)
  const menu = (p.menu_sections || []).map(sec => ({
    section: sec.name,
    items: (sec.items || []).map(item => ({
      name:  item.name,
      desc:  item.description || '',
      price: item.price,
    })),
  }));

  const priceRange = p.price_range || '₦₦';

  return {
    id:       String(p.id),
    slug:     p.slug || makeSlug(p.name),
    name:     p.name,
    cat:      p.category,
    area:     p.area,
    city:     p.city || 'Abuja',
    about:    p.about || '',
    price:    priceRange,
    priceNum: PRICE_NUM[priceRange] ?? 2,
    rating:   parseFloat(p.avg_rating ?? p.rating) || 0,
    reviews:  parseInt(p.review_count ?? p.reviews) || 0,
    phone:    p.phone     || '',
    whatsapp: p.whatsapp  || '',
    instagram:p.instagram || '',
    website:  p.website   || '',
    photos,
    tags:     (p.vibe || []).map(v => String(v).trim()),
    vibe:     (p.vibe || []).map(v => String(v).trim()),
    features: p.amenities   || [],  // amenities shown as ✓ checkmarks
    badge:    parseFloat(p.avg_rating ?? p.rating) >= 4.5 ? 'hot' : null,
    special:  p.special     || '',
    avg_spend:p.avg_spend   || '',
    hours,
    menu,
    // map coords
    lat: p.latitude  ? parseFloat(p.latitude)  : null,
    lng: p.longitude ? parseFloat(p.longitude) : null,
    // street address shown above map in detail view
    address: p.address || '',
    // SEO
    seo_title:       p.seo_title       || '',
    seo_description: p.seo_description || '',
    seo_keywords:    p.seo_keywords    || '',
    // Menu visibility — admin-controlled toggle; default true when field absent
    show_menu: p.show_menu !== false && p.show_menu !== 0 && p.show_menu !== '0',
    // Hierarchy fields
    parent_place_id: p.parent_place_id ? parseInt(p.parent_place_id) : null,
    source_place_id: p.source_place_id ? parseInt(p.source_place_id) : null,
    is_branch: p.is_branch ? Boolean(p.is_branch) : false,
    // fallbacks for cards that use emoji/bg
    emoji: '📍',
    bg:    '#1a1a2e',
  };
}

// ── Day helpers for event scheduling ─────────────────────────────
// JS getDay(): 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
const _JS_DAYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

// Convert JS getDay() index → week order index (Mon=0 … Sun=6)
function _toWkIdx(jsDay) { return jsDay === 0 ? 6 : jsDay - 1; }

// Extract the first weekday mentioned in a when_label string.
// Returns JS getDay() index (0-6) or -1 if none found.
function _extractDay(whenLabel) {
  const w = (whenLabel || '').toLowerCase();
  for (let i = 0; i < _JS_DAYS.length; i++) {
    if (w.includes(_JS_DAYS[i])) return i;
  }
  return -1;
}

export function normalizeEvent(e) {
  const vibe  = (e.vibe || []).map(v => String(v).trim());
  // Normalise badge: lowercase + treat null/empty/legacy 'open' as 'none'.
  const rawBadge = (e.badge || 'none').toLowerCase();
  const when  = (e.when_label || '').toLowerCase();

  const now            = new Date();
  const todayJsDay     = now.getDay();                       // 0=Sun … 6=Sat
  const todayWkIdx     = _toWkIdx(todayJsDay);               // Mon=0 … Sun=6
  const tomorrowJsDay  = (todayJsDay + 1) % 7;
  const tomorrowWkIdx  = _toWkIdx(tomorrowJsDay);
  const isSatOrSun     = todayJsDay === 0 || todayJsDay === 6;
  const recurringDay   = _extractDay(e.when_label);          // JS day index of event, or -1
  const recurringWkIdx = recurringDay !== -1 ? _toWkIdx(recurringDay) : -1;

  // Is this a recurring weekday event (has a day name in when_label)?
  const isRecurring = recurringDay !== -1;

  // Event is TONIGHT (today):
  //   - Recurring event   → ONLY if the extracted weekday matches today exactly.
  //   - Non-recurring event → when_label contains "tonight" or "today", OR
  //     start_date matches today's date (YYYY-MM-DD).
  //   Note: the badge field is decorative (hot/new/etc.) and does NOT drive
  //   tonight detection — that way admins can set any badge without affecting
  //   the "tonight" tab or the TONIGHT chip on cards.
  const todayDateStr = now.toISOString().slice(0, 10); // e.g. "2025-06-01"
  const isToday = isRecurring
    ? recurringDay === todayJsDay
    : (when.includes('tonight') || when.includes('today') || e.start_date === todayDateStr);

  // Event is TOMORROW:
  //   - Recurring event   → extracted weekday matches tomorrow's JS day.
  //   - Non-recurring     → when_label says "tomorrow".
  const isTomorrow = isRecurring
    ? recurringDay === tomorrowJsDay
    : when.includes('tomorrow');

  // Event is THIS WEEK (Mon–Sun of the current week) if:
  //   - it is today or tomorrow, OR
  //   - it's a recurring event whose weekday still comes later this week
  //     (wkIdx > today's wkIdx means the day hasn't happened yet this week).
  //   Thursday (wkIdx 3) on Friday (wkIdx 4): 3 > 4 → false → excluded ✓
  //   Saturday (wkIdx 5) on Friday (wkIdx 4): 5 > 4 → true  → included ✓
  const isThisWeek =
    isToday ||
    isTomorrow ||
    (isRecurring && recurringWkIdx > tomorrowWkIdx);

  // Compute the badge to show on cards / detail:
  //   - If happening tonight  → always show 'tonight' (overrides any CMS badge).
  //   - If badge is 'none' (or null/empty/old 'open' sentinel) → show nothing (null).
  //   - Otherwise             → show the CMS-set badge (e.g. 'hot', 'new').
  const displayBadge = isToday
    ? 'tonight'
    : (rawBadge === 'none' ? null : rawBadge);

  // Derive filter tabs
  const tab = ['all'];
  if (isToday)    tab.push('tonight');
  if (isTomorrow) tab.push('tomorrow');
  if (isThisWeek) tab.push('this-week');
  // Legacy category tabs (kept for any other consumers)
  if (isThisWeek && (isSatOrSun || recurringWkIdx >= 5 || when.includes('weekend'))) tab.push('weekend');
  if (vibe.some(v => ['food','foodie','brunch','dinner'].includes(v.toLowerCase()))) tab.push('food');
  if (vibe.some(v => ['cultural','culture','art','jazz'].includes(v.toLowerCase()))) tab.push('cultural');
  if (vibe.some(v => ['outdoor','nature','lake','park'].includes(v.toLowerCase()))) tab.push('outdoor');
  if (vibe.some(v => ['nightlife','party','clubbing'].includes(v.toLowerCase())) || (e.category||'').toLowerCase() === 'nightlife') tab.push('nightlife');

  return {
    id:         String(e.id),
    slug:       e.slug || makeSlug(e.name),
    name:       e.name,
    category:   e.category   || '',
    venue:      e.venue_name || '',
    venueId:    e.venue_id   ? String(e.venue_id) : null,
    area:       e.area       || '',
    when:       e.when_label || '',
    time:       e.time_label || (
                  e.start_time
                    ? fmt24to12(e.start_time.substring(0, 5)) +
                      (e.end_time ? ' – ' + fmt24to12(e.end_time.substring(0, 5)) : '')
                    : ''),
    ticket:     Number(e.ticket_price ?? 0),
    vipTicket:  e.vip_min    ?? 0,
    dress:      e.venue_dress_code || e.dress_code || '',
    contact_phone: e.contact_phone || e.venue_phone || '',
    badge:      displayBadge,
    tab,
    isThisWeek,
    about:      e.about      || '',
    img:        e.cover_image|| '',
    vibe,
    // SEO
    seo_title:       e.seo_title       || '',
    seo_description: e.seo_description || '',
    seo_keywords:    e.seo_keywords    || '',
    // venue map coords + address (joined from places in the PHP query)
    lat:     e.venue_lat     ? parseFloat(e.venue_lat) : null,
    lng:     e.venue_lng     ? parseFloat(e.venue_lng) : null,
    address: e.venue_address || '',
    emoji: '🎉',
    bg:    '#1a0a2e',
  };
}

export function normalizeActivity(a) {
  // pricing is a JSON array of {label, price} — already decoded by PHP expand()
  const pricing = (() => {
    if (Array.isArray(a.pricing)) return a.pricing;
    if (typeof a.pricing === 'string') {
      try { return JSON.parse(a.pricing) || []; } catch { return []; }
    }
    return [];
  })();

  return {
    id:       String(a.id),
    slug:     a.slug || makeSlug(a.name),
    name:     a.name,
    cat:      a.category,
    category: a.category,
    venue:    a.venue_name   || '',
    venueId:  a.venue_id     ? String(a.venue_id) : null,
    area:     a.area         || '',
    duration: a.duration     || '',
    price:    a.base_price   ?? 0,
    pricing,
    booking:  Number(a.booking_required) !== 0,
    contact_phone: a.contact_phone || a.venue_phone || '',
    about:    a.about        || '',
    img:      a.cover_image  || '',
    vibe:     a.vibe         || [],
    extras:   (a.extras || []).map(ex => ({ label: ex.label, price: ex.price })),
    // SEO
    seo_title:       a.seo_title       || '',
    seo_description: a.seo_description || '',
    seo_keywords:    a.seo_keywords    || '',
    // venue map coords + address (joined from places in the PHP query)
    lat:     a.venue_lat     ? parseFloat(a.venue_lat) : null,
    lng:     a.venue_lng     ? parseFloat(a.venue_lng) : null,
    address: a.venue_address || '',
    emoji: '🎯',
    bg:    '#0a1a2e',
  };
}

export function normalizeCommunity(c) {
  // Build photos array from cover_image + community_images
  const photos = [];

  // Add cover image first if it exists
  if (c.cover_image) photos.push(c.cover_image);

  // Add all images from community_images table
  if (Array.isArray(c.images) && c.images.length > 0) {
    [...c.images]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .forEach(img => {
        const imgUrl = img.url || img.image_url || img.path;
        if (imgUrl && !photos.includes(imgUrl)) {
          photos.push(imgUrl);
        }
      });
  }

  // Convert vibe array to string array (handle both formats)
  const vibes = Array.isArray(c.vibe)
    ? c.vibe.map(v => String(v).trim()).filter(Boolean)
    : [];

  // Parse other_fees if it's a JSON string
  let otherFees = {};
  if (c.other_fees) {
    if (typeof c.other_fees === 'string') {
      try { otherFees = JSON.parse(c.other_fees) || {}; } catch { otherFees = {}; }
    } else {
      otherFees = c.other_fees;
    }
  }

  return {
    id: String(c.id),
    slug: c.slug,
    name: c.name,
    description: c.description || '',
    about: c.about || '',
    logo: c.logo || '',
    cover_image: c.cover_image || '',
    category: c.category || '',
    sub_category: c.sub_category || '',
    email: c.email || '',
    phone: c.phone || '',
    instagram: c.instagram || '',
    website: c.website || '',
    city: c.city || 'Abuja',
    area: c.area || '',
    address: c.address || '',
    latitude: c.latitude ? parseFloat(c.latitude) : null,
    longitude: c.longitude ? parseFloat(c.longitude) : null,
    vibe: vibes,
    member_count: c.member_count || 0,
    is_free: c.is_free || false,
    membership_fee: c.membership_fee || 0,
    other_fees: otherFees,
    status: c.status || 'draft',
    photos,
    activities: Array.isArray(c.activities) ? c.activities : [],
    events: Array.isArray(c.events) ? c.events : [],
    schedule: Array.isArray(c.schedule) ? c.schedule : [],
    // SEO
    seo_title: c.seo_title || '',
    seo_description: c.seo_description || '',
    seo_keywords: c.seo_keywords || '',
  };
}

export function normalizeItinerary(it) {
  const days = (it.days || []).map(d => ({
    label: d.label || `Day ${d.day_number}`,
    note:  d.note  || '',
    date:  d.day_date || '',
    stops: (d.stops || []).map(s => ({
      type:     s.stop_type,
      id:       String(s.ref_id || ''),
      isCustom: Boolean(s.is_custom),
      time:     s.stop_time ? s.stop_time.substring(0, 5) : '',
      note:     s.note  || '',
      // Resolved display fields (pre-fetched by PHP; custom stops use custom_data)
      name:  s.ref_name  || '',
      area:  s.ref_area  || '',
      venue: s.ref_venue || '',
      img:   s.ref_img   || '',
      price: s.ref_price ?? 0,
      customCost: s.custom_cost ?? null,
      costMode:   s.cost_mode   || 'person',
    })),
  }));

  const rsvpFields = (it.rsvp_fields || []).map(f => {
    // DB column is `field_type`; `f.type` is a fallback for any in-memory objects
    const fieldType = f.field_type || f.type || 'text';
    // `options` may be a JSON string from the DB or an already-parsed array
    let options = [];
    const rawOpts = f.options;
    if (Array.isArray(rawOpts)) {
      options = rawOpts;
    } else if (typeof rawOpts === 'string' && rawOpts.trim()) {
      try { options = JSON.parse(rawOpts); } catch { options = []; }
    }
    return {
      id:       String(f.id),
      label:    f.label    || '',
      type:     fieldType,
      required: Boolean(f.required),
      options,
    };
  });

  return {
    id:               String(it.id),
    slug:             it.slug || makeSlug(it.name),
    status:           it.status      || 'active',
    name:             it.name,
    category:         it.category,
    city:             it.city        || 'Abuja',
    creator:          it.creator     || '',
    tagline:          it.tagline     || '',
    description:      it.description || '',
    people:           it.people      || 2,
    theme:            it.theme       || 'forest',
    cover:            it.cover_image || '',
    privacy:          it.privacy     || 'public',
    // RSVP
    rsvp:             Boolean(it.rsvp_enabled),
    rsvpCount:        Number(it.rsvp_count)  || 0,
    rsvpMax:          Number(it.rsvp_max)    || 0,
    rsvp_note:        it.rsvp_note     || '',
    rsvp_deadline:    it.rsvp_deadline || '',
    allowDaySelection: Boolean(it.allow_day_selection ?? true),
    rsvpFields,
    days,
  };
}

// ─── CMS Public API ───────────────────────────────────────────────────────────
//
// paginate() returns  { data:[...], total, page, per_page, last_page }
// json_ok() wraps it  { success:true, data:{ data:[...], total, … } }
// So for list calls:  r.data       = pagination envelope
//                     r.data.data  = the items array
// For single calls:   r.data       = the item object directly

export const cmsApi = {
  // Places
  getPlaces: params =>
    cmsReqCached('/public/places?' + new URLSearchParams(params || {}))
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizePlace) })),

  getPlace: slug =>
    cmsReq(`/public/places/${encodeURIComponent(slug)}`)
      .then(r => ({ ...r, data: normalizePlace(r.data) })),

  // Events
  getEvents: params =>
    cmsReqCached('/public/events?' + new URLSearchParams(params || {}))
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizeEvent) })),

  getEvent: slug =>
    cmsReq(`/public/events/${encodeURIComponent(slug)}`)
      .then(r => ({ ...r, data: normalizeEvent(r.data) })),

  // Activities
  getActivities: params =>
    cmsReqCached('/public/activities?' + new URLSearchParams(params || {}))
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizeActivity) })),

  getActivity: slug =>
    cmsReq(`/public/activities/${encodeURIComponent(slug)}`)
      .then(r => ({ ...r, data: normalizeActivity(r.data) })),

  getCommunities: params =>
    cmsReqCached('/public/communities?' + new URLSearchParams(params || {}))
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizeCommunity) })),

  getCommunity: slug =>
    cmsReq(`/public/communities/${encodeURIComponent(slug)}`)
      .then(r => ({ ...r, data: normalizeCommunity(r.data) })),

  // Itineraries
  getItineraries: params =>
    cmsReq('/public/itineraries?' + new URLSearchParams(params || {}))
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizeItinerary) })),

  getItinerary: slug =>
    cmsReq(`/public/itineraries/${encodeURIComponent(slug)}`)
      .then(r => ({ ...r, data: normalizeItinerary(r.data) })),

  /** All itineraries by this creator regardless of privacy/status — for profile page */
  getMyItineraries: creator =>
    cmsReq(`/public/my-itineraries?creator=${encodeURIComponent(creator)}`)
      .then(r => (r.data || []).map(normalizeItinerary)),

  /** All RSVP submissions received on the creator's itineraries — for profile RSVPs tab */
  getMyRsvps: creator =>
    cmsReq(`/public/my-rsvps?creator=${encodeURIComponent(creator)}`)
      .then(r => r.data || []),

  /** Creator deletes a specific RSVP submission */
  deleteRsvp: (submissionId, creator) =>
    cmsReq(`/public/rsvp/${submissionId}?creator=${encodeURIComponent(creator)}`, { method: 'DELETE' })
      .then(r => r.data),

  // Search — returns { places:[], events:[], activities:[] } directly (not paginated)
  search: q =>
    cmsReq(`/public/search?q=${encodeURIComponent(q)}`).then(r => ({
      places:     (r.data.places     || []).map(normalizePlace),
      events:     (r.data.events     || []).map(normalizeEvent),
      activities: (r.data.activities || []).map(normalizeActivity),
    })),

  // Reviews
  getReviews: params =>
    cmsReq('/public/reviews?' + new URLSearchParams(params || {}))
      .then(r => ({ data: r.data || [] })),

  submitReview: data =>
    cmsReq('/public/reviews', { method: 'POST', body: JSON.stringify(data) })
      .then(r => r.data),

  // Likes
  toggleLike: data =>
    cmsReq('/public/likes', { method: 'POST', body: JSON.stringify(data) })
      .then(r => r.data),

  getUserLikes: uid =>
    cmsReq(`/public/likes?uid=${encodeURIComponent(uid)}`)
      .then(r => ({ data: r.data || [] })),

  // Pending stops
  submitPendingStop: data =>
    cmsReq('/public/pending-stops', { method: 'POST', body: JSON.stringify(data) })
      .then(r => r.data),

  // Customer registration — upsert by uid each time user signs in
  saveCustomer: data =>
    cmsReq('/public/customers', { method: 'POST', body: JSON.stringify(data) })
      .then(r => r.data),

  // Cities — active cities list for the city selector
  getCities: () =>
    cmsReqCached('/public/cities').then(r => r.data || []),

  // Areas — areas for a city (or all areas if no city given)
  getAreas: (city) =>
    cmsReqCached('/public/areas' + (city ? '?city=' + encodeURIComponent(city) : ''))
      .then(r => r.data || []),

  // Site settings — always fetch fresh so toggles (blog_enabled, maintenance_mode)
  // take effect immediately without waiting for the cache TTL.
  getSiteSettings: () =>
    cmsReq('/public/settings', { cache: 'no-store' }).then(r => r.data || {}),

  // Featured promotions — returns [] when featured_enabled = '0' on the server.
  // cache: 'no-store' bypasses browser HTTP cache so a toggle change in the CMS
  // is visible immediately on the next fetch without waiting for cache expiry.
  getFeatured: () =>
    cmsReq('/public/featured', { cache: 'no-store' }).then(r => r.data || []),

  // Blog — public listing and post detail
  getBlogPosts: (params) =>
    cmsReqCached('/public/blog?' + new URLSearchParams(params || {}))
      .then(r => ({ ...r.data, data: r.data.data || [] })),

  getBlogPost: (slug) =>
    cmsReq(`/public/blog/${encodeURIComponent(slug)}`)
      .then(r => r.data),

  // Blog comments
  getBlogComments: (postId) =>
    cmsReq(`/public/blog/${postId}/comments`).then(r => r.data || []),

  submitBlogComment: (postId, payload) =>
    cmsReq(`/public/blog/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then(r => r),

  // Hierarchy — branches and parent places
  getPlaceBranches: (placeId) =>
    cmsReq(`/public/places/${placeId}/branches`)
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizePlace) })),

  getPlaceChildren: (placeId) =>
    cmsReq(`/public/places/${placeId}/children`)
      .then(r => ({ ...r.data, data: (r.data.data || []).map(normalizePlace) })),

  getPlaceParent: (placeId) =>
    cmsReq(`/public/places/${placeId}/parent`)
      .then(r => r.data ? normalizePlace(r.data) : null),

  // Zén Guide AI chat
  chat: (message, city, history = []) =>
    cmsReq('/public/chat', {
      method: 'POST',
      body: JSON.stringify({ message, city, history }),
    }).then(r => r.data),
};

// ─── Itineraries, RSVP & Contact — all on the CMS public API ─────────────────

export const api = {
  // Itineraries
  getItinerary:     id       => cmsReq(`/public/itineraries/${id}`).then(r => r.data),
  saveItinerary:    data     => cmsReq('/public/itineraries', { method: 'POST', body: JSON.stringify(data) }).then(r => r.data),
  updateItinerary:  (id, data) => cmsReq(`/public/itineraries/${id}`, { method: 'PUT',    body: JSON.stringify(data) }).then(r => r.data),
  deleteItinerary:  (id, creator) => cmsReq(`/public/itineraries/${id}`, { method: 'DELETE', body: JSON.stringify({ creator }) }).then(r => r.data),

  // RSVPs
  submitRsvp: data => cmsReq('/public/rsvp', { method: 'POST', body: JSON.stringify(data) }).then(r => r.data),

  // Contact
  contact: data => cmsReq('/public/contact', { method: 'POST', body: JSON.stringify(data) }).then(r => r),
};
